/**
 * critters — ambient LIFE: a few small soft glow motes that TRAVERSE the whole
 * scene horizontally (distant birds / bugs / fish crossing the air), recycling
 * at the far edge with a fresh vertical lane + speed. DISTINCT from `fireflies`
 * (which wander in place around a spawn point) — critters cross the screen.
 *
 * Each mote rides a horizontal velocity across the camera region (`ctx.region`
 * sizes the lane band; `ctx.getCameraView` gives the live span each frame so it
 * keeps crossing as the camera pans). On exit it wraps to the opposite edge with
 * a new y inside the normalized [bandYMin, bandYMax] lane + a new speed.
 *
 * Pure soft-light sprites (createGlowSprite) so it ports behind IRenderer like
 * the rest of the kit. mooded:false — ambient life, not a mood-dimmed bed.
 *
 * Reduce-motion: the mote tick is this builder's OWN motion (the AtmosphereLayer
 * modulation freeze does NOT cover it), so when reduced we place the motes STATIC
 * (spread across the band) and never attach the tick. Read the pref ONCE at build.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { seededRand } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import type { ISprite } from '../../../rendering/IRenderer';
import { lerp } from '../../../../shared/utils/MathUtils';

/** One traversing mote — sprite + its live world position, lane Y + signed vx. */
interface Critter {
  sprite: ISprite;
  x: number;
  y: number;
  /** Signed horizontal velocity (px/sec); sign is the travel direction. */
  vx: number;
  /** Transient flee multiplier (world-combat reactivity, spec §2.4): a pulse
   *  inside the radius sets this to 2–3× and flips the travel direction away
   *  from the pulse; the tick decays it back to 1 (lane speed) over ~2s. */
  boost: number;
}

/** Flee-boost decay time constant — boost eases back to 1 over ~2s. */
const FLEE_DECAY_TAU_MS = 600;
/** Below this excursion the boost snaps to exactly 1 (skip-churn parity). */
const FLEE_SNAP_EPSILON = 0.02;

const buildCritters: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'critters') return null;
  const { region } = ctx;
  const reduce = getMotionPrefs().reduceMotion; // read ONCE at build time

  // Lane band in world-Y: bandYMin/bandYMax are normalized within the region.
  const bandY0 = region.y0 + layer.bandYMin * (region.y1 - region.y0);
  const bandY1 = region.y0 + layer.bandYMax * (region.y1 - region.y0);

  // Pick a per-critter travel direction. 'both' splits by index parity so a
  // flock reads as cross-traffic rather than a single migrating column.
  const dirSign = (i: number): number => {
    if (layer.direction === 'rtl') return -1;
    if (layer.direction === 'ltr') return 1;
    return i % 2 === 0 ? 1 : -1; // 'both'
  };

  const critters: Critter[] = [];
  for (let i = 0; i < layer.count; i += 1) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const s = ctx.seed + i * 9.73;
    // Spread starting X across the region so they don't all enter at once.
    const x = lerp(region.x0, region.x1, seededRand(s));
    const y = lerp(bandY0, bandY1, seededRand(s + 1));
    const size = lerp(layer.sizeMin, layer.sizeMax, seededRand(s + 2));
    const color = layer.palette[Math.floor(seededRand(s + 3) * layer.palette.length) % layer.palette.length];
    const speed = lerp(layer.speedMin, layer.speedMax, seededRand(s + 4));
    const sprite = createGlowSprite({
      x, y, size, color, alpha: 1,
      textureKey: layer.textureKey, blendMode: layer.blendMode, parent: ctx.layerContainer,
    });
    if (!sprite) break; // texture not ready — quieter scene, never a crash
    critters.push({ sprite, x, y, vx: speed * dirSign(i), boost: 1 });
  }

  if (!critters.length) return null;

  // Reduced motion: leave the motes placed statically across the band, no tick.
  if (reduce) return makeContentHandle({ sprites: critters.map((c) => c.sprite) });

  // Recycle a mote to the opposite edge with a fresh lane Y + speed when it
  // leaves the live camera span (region width as a fallback when no camera).
  let recycleSeed = ctx.seed + 1000;
  const respawn = (c: Critter, spanX0: number, spanX1: number) => {
    recycleSeed += 1;
    const enteringFromLeft = c.vx > 0;
    c.x = enteringFromLeft ? spanX0 : spanX1;
    c.y = lerp(bandY0, bandY1, seededRand(recycleSeed));
    const speed = lerp(layer.speedMin, layer.speedMax, seededRand(recycleSeed + 0.5));
    c.vx = speed * (enteringFromLeft ? 1 : -1);
    c.boost = 1; // a recycled mote enters at calm lane speed
  };

  const tick = (dtMs: number) => {
    const v = ctx.getCameraView();
    // Live camera span (plus a sprite-width of slack) or the static region box.
    const spanX0 = v ? v.centerX - v.width / 2 : region.x0;
    const spanX1 = v ? v.centerX + v.width / 2 : region.x1;
    const margin = (spanX1 - spanX0) * 0.08; // wrap a touch off-screen, not at the edge
    const dt = dtMs / 1000;
    for (const c of critters) {
      c.x += c.vx * c.boost * dt;
      // Flee decay — ease the transient boost back to authored lane speed.
      if (c.boost !== 1) {
        c.boost = 1 + (c.boost - 1) * Math.exp(-dtMs / FLEE_DECAY_TAU_MS);
        if (Math.abs(c.boost - 1) < FLEE_SNAP_EPSILON) c.boost = 1;
      }
      if (c.vx > 0 && c.x > spanX1 + margin) respawn(c, spanX0 - margin, spanX1 + margin);
      else if (c.vx < 0 && c.x < spanX0 - margin) respawn(c, spanX0 - margin, spanX1 + margin);
      c.sprite.setPosition(c.x, c.y);
    }
  };
  const unsub = ctx.addTick(tick);

  // Positional pulse (world-combat reactivity): critters inside the radius
  // FLEE — 2–3× speed directed away from the pulse x, decaying back to lane
  // speed in the tick above. Per-critter field mutation only — no allocation.
  const onPulse = (px: number, py: number, intensity: number, radius: number) => {
    const kick = Math.max(0, Math.min(1, intensity));
    for (const c of critters) {
      if (Math.hypot(c.x - px, c.y - py) > radius) continue;
      c.boost = 2 + kick; // 2–3× by pulse strength
      c.vx = Math.abs(c.vx) * (c.x >= px ? 1 : -1); // away from the pulse
    }
  };

  return makeContentHandle({
    sprites: critters.map((c) => c.sprite),
    onDestroy: () => { unsub(); },
    onPulse,
  });
};

registerEffect({ kind: 'critters', build: buildCritters, depth: Z_LAYERS.ATMOSPHERE, mooded: false });
